import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/api/client";
import { produitsService } from "@/api/services";
import { DataTable, type Column } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { Filters } from "@/components/common/Filters";
import { LoadingState } from "@/components/common/LoadingState";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchInput } from "@/components/common/SearchInput";
import { StatusBadge } from "@/components/common/StatusBadge";
import { InventoryScanPanel } from "@/components/inventory/InventoryScanPanel";
import { useI18n } from "@/i18n/I18nProvider";
import type { Product } from "@/types/domain";

type StatutFilter = "tous" | "actifs" | "inactifs";
type StockFilter = "tous" | "disponible" | "indisponible" | "en_commande";

const columns: Column<Product>[] = [
  {
    key: "sku",
    header: "SKU",
    mono: true,
    render: (produit) => (
      <Link
        to={`/produits/${produit.id}`}
        className="font-medium hover:underline"
      >
        {produit.sku}
      </Link>
    ),
  },
  {
    key: "description",
    header: "Description",
    render: (produit) => produit.description_officielle,
  },
  {
    key: "physique",
    header: "Quantité physique",
    align: "right",
    render: (produit) => produit.quantite_physique,
  },
  {
    key: "reservee",
    header: "Quantité réservée",
    align: "right",
    render: (produit) => produit.quantite_reservee,
  },
  {
    key: "disponible",
    header: "Quantité disponible",
    align: "right",
    render: (produit) => produit.quantite_disponible,
  },
  {
    key: "commande",
    header: "Quantité en commande",
    align: "right",
    render: (produit) => produit.quantite_en_commande,
  },
  {
    key: "emplacement",
    header: "Emplacement",
    render: (produit) => produit.emplacement || "—",
  },
  {
    key: "statut",
    header: "Statut",
    render: (produit) => (
      <StatusBadge
        status={produit.actif ? "Actif" : "Inactif"}
        tone={produit.actif ? "success" : "neutral"}
      />
    ),
  },
];

export default function InventairePage() {
  const { t } = useI18n();
  const [produits, setProduits] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [query, setQuery] = useState("");
  const [statut, setStatut] = useState<StatutFilter>("tous");
  const [stock, setStock] = useState<StockFilter>("tous");

  const loadProduits = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      setProduits(await produitsService.list());
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError("Impossible de charger l'inventaire.", "unexpected")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProduits();
  }, [loadProduits]);

  const produitsFiltres = useMemo(() => {
    const recherche = query.trim().toLocaleLowerCase("fr-CA");

    return produits.filter((produit) => {
      const correspondRecherche =
        !recherche ||
        produit.sku.toLocaleLowerCase("fr-CA").includes(recherche) ||
        produit.description_officielle
          .toLocaleLowerCase("fr-CA")
          .includes(recherche) ||
        (produit.emplacement ?? "")
          .toLocaleLowerCase("fr-CA")
          .includes(recherche);
      const correspondStatut =
        statut === "tous" ||
        (statut === "actifs" && produit.actif) ||
        (statut === "inactifs" && !produit.actif);
      const correspondStock =
        stock === "tous" ||
        (stock === "disponible" && produit.quantite_disponible > 0) ||
        (stock === "indisponible" && produit.quantite_disponible <= 0) ||
        (stock === "en_commande" && produit.quantite_en_commande > 0);

      return correspondRecherche && correspondStatut && correspondStock;
    });
  }, [produits, query, statut, stock]);

  const filtresActifs = Boolean(query.trim()) || statut !== "tous" || stock !== "tous";

  return (
    <div className="space-y-6 p-6" data-testid="inventaire-page">
      <PageHeader
        title={t.modules.inventaire.title}
        subtitle="Consultation des quantités fournies par le système d'inventaire."
      />

      {!loading && !error && (
        <InventoryScanPanel
          products={produits}
          resolveIdentifier={produitsService.resolveIdentifier}
        />
      )}

      {!loading && !error && produits.length > 0 && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Rechercher par SKU, description ou emplacement..."
            testId="inventaire-recherche"
            className="w-full lg:max-w-md"
          />
          <Filters
            label="Statut"
            value={statut}
            onChange={(value) => setStatut(value as StatutFilter)}
            testId="inventaire-statut"
            options={[
              { value: "tous", label: "Tous les statuts" },
              { value: "actifs", label: "Actifs" },
              { value: "inactifs", label: "Inactifs" },
            ]}
          />
          <Filters
            label="Stock"
            value={stock}
            onChange={(value) => setStock(value as StockFilter)}
            testId="inventaire-stock"
            options={[
              { value: "tous", label: "Toutes les quantités" },
              { value: "disponible", label: "Disponible" },
              { value: "indisponible", label: "Indisponible" },
              { value: "en_commande", label: "En commande" },
            ]}
          />
        </div>
      )}

      {loading ? (
        <LoadingState label="Chargement de l'inventaire..." rows={6} />
      ) : error ? (
        <ErrorState
          title="Impossible de charger l'inventaire."
          description={error.message}
          retryLabel="Réessayer"
          onRetry={() => void loadProduits()}
        />
      ) : produits.length === 0 ? (
        <EmptyState
          title="Inventaire vide"
          description="Aucun produit n'est actuellement disponible en consultation."
        />
      ) : produitsFiltres.length === 0 ? (
        <EmptyState
          title="Aucun résultat"
          description="Aucun produit ne correspond à la recherche et aux filtres sélectionnés."
          action={
            filtresActifs ? (
              <button
                type="button"
                className="ops-focus mt-2 rounded-sm border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
                onClick={() => {
                  setQuery("");
                  setStatut("tous");
                  setStock("tous");
                }}
              >
                Réinitialiser les filtres
              </button>
            ) : undefined
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={produitsFiltres}
          rowKey={(produit) => produit.id}
          testId="inventaire-table"
          className="rounded-sm border border-border"
        />
      )}

      {!loading && !error && produits.length > 0 && (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {produitsFiltres.length} produit{produitsFiltres.length > 1 ? "s" : ""}
          {filtresActifs ? " affiché(s) après filtrage" : " affiché(s)"}.
        </p>
      )}
    </div>
  );
}
